import {ContentEditableEditorPlugin} from '@frontapp/editor-html';
import {isUndefined, noop} from 'lodash';

import {
  escapeRegex,
  isBasicLink,
  splitBasicLinks,
} from '../../../../../../libs/shared/helpers/src/utilities/stringHelpers';

type CharacterShortcutPluginReplaceHandler = (shortcutText: string) => void;

interface CharacterShortcutPluginOptions {
  onReplace?: CharacterShortcutPluginReplaceHandler;
}

export function createCharacterShortcutPlugin(
  options: CharacterShortcutPluginOptions = {},
): ContentEditableEditorPlugin {
  return {
    processText: (element, textNodeAndCursorPositions) => {
      const document = element.ownerDocument;
      if (!document) {
        return;
      }

      Array.from(textNodeAndCursorPositions).forEach(({node, cursorPosition}) => {
        processCharShortcuts(node, cursorPosition, options.onReplace);
      });
    },
  };
}

interface CharacterShortcutDeclaration {
  shortcutText: string;
  replacementText: string;
}

const charShortcuts: readonly CharacterShortcutDeclaration[] = [
  {shortcutText: '-->', replacementText: '→'},
  {shortcutText: '<--', replacementText: '←'},
  {shortcutText: '<->', replacementText: '↔'},
];

const charShortcutRegex = new RegExp(
  `(${charShortcuts.map(({shortcutText}) => escapeRegex(shortcutText)).join('|')})`,
);

const charShortcutMap = new Map(
  charShortcuts.map(({shortcutText, replacementText}) => [shortcutText, replacementText]),
);

export function processCharShortcuts(
  text: Text,
  cursorPosition?: number,
  onReplace: CharacterShortcutPluginReplaceHandler = noop,
) {
  const {textContent} = text;
  if (!textContent) {
    return;
  }

  let offset = 0;
  const linkParts = splitBasicLinks(textContent);
  const newText = linkParts
    .map((part) => {
      // Do not replace anything inside links.
      if (isBasicLink(part)) {
        offset += part.length;
        return part;
      }

      // Otherwise loop on part split by charShortcutRegex.
      return part
        .split(charShortcutRegex)
        .map((maybeShortcutText) => {
          offset += maybeShortcutText.length;
          const replacementText = charShortcutMap.get(maybeShortcutText);

          if (
            isUndefined(replacementText) ||
            !isSafeToMatch(maybeShortcutText, textContent, offset, cursorPosition)
          ) {
            return maybeShortcutText;
          }

          onReplace(maybeShortcutText);
          return replacementText;
        })
        .join('');
    })
    .join('');

  if (newText !== textContent) {
    text.replaceData(0, textContent.length, newText);
  }
}

function isSafeToMatch(charShortcut: string, fullString: string, offset: number, cursorPosition?: number) {
  // Grab the text before and after the charShortcut.
  const beforeCharShortcut = fullString.substring(0, offset - charShortcut.length);
  const afterCharShortcut = fullString.slice(offset);
  const hasSpaceAfter = afterCharShortcut.match(/^\s/) !== null;
  const hasSpaceBefore = beforeCharShortcut.match(/\s$/) !== null;
  const isCursorInNode = cursorPosition !== undefined;
  const isCursorInCharShortcut =
    cursorPosition !== undefined &&
    offset - charShortcut.length <= cursorPosition &&
    cursorPosition <= offset;

  // Never replace text on which cursor is.
  if (isCursorInCharShortcut) {
    return false;
  }

  // If the charShortcut is surrounded with spaces, process it.
  if (hasSpaceAfter && hasSpaceBefore) {
    return true;
  }

  // If it's a leading charShortcut with a space after it, process it.
  if (beforeCharShortcut.length === 0 && hasSpaceAfter) {
    return true;
  }

  // If it's a a leading and a tailing charShortcut with a space before, and cursor is not on node, process it.
  if (afterCharShortcut.length === 0 && hasSpaceBefore && !isCursorInNode) {
    return true;
  }

  // If it's both a a leading and a tailing charShortcut, and cursor is not on node, process it.
  if (afterCharShortcut.length === 0 && beforeCharShortcut.length === 0 && !isCursorInNode) {
    return true;
  }

  return false;
}
